import { TrapezoidalRule } from '@bioavail/shared-types';
import type {
  ConcentrationTimePoint,
  TrapezoidalSegment,
  BlqRule,
} from '@bioavail/shared-types';
import { applyBlqRule } from './blq';
import { findCmaxIndex } from './cmax';

/**
 * Trapezoidal integration from 0 to last time (PSD §4.2.3).
 * Supports linear, linear-up/log-down, and linear-log rules.
 * Returns the AUC(0-t) plus per-segment detail for chart overlays.
 */
export function computeAucZeroToT(
  inputPoints: ConcentrationTimePoint[],
  rule: TrapezoidalRule,
  blqRule: BlqRule,
  lloq?: number,
): { aucZeroToT: number; segments: TrapezoidalSegment[]; missingLloq: boolean } {
  const points = [...inputPoints].sort((a, b) => a.time - b.time);

  if (points.length < 2) {
    throw new Error('At least 2 data points required for integration.');
  }

  // Cmax index (earliest on tie) — drives the linear-up/log-down split.
  const cmaxIndex = findCmaxIndex(points);

  const segments: TrapezoidalSegment[] = [];
  let cumulative = 0;
  let anyMissingLloq = false;

  for (let i = 0; i < points.length - 1; i++) {
    const rawP1 = points[i]!;
    const rawP2 = points[i + 1]!;
    const dt = rawP2.time - rawP1.time;
    if (dt === 0) {
      // Zero-width segment — skip (no error).
      continue;
    }

    const b1 = applyBlqRule(rawP1, blqRule, lloq);
    const b2 = applyBlqRule(rawP2, blqRule, lloq);
    if (b1.missingLloq || b2.missingLloq) anyMissingLloq = true;
    const c1 = b1.effectiveConcentration;
    const c2 = b2.effectiveConcentration;

    let method: 'linear' | 'log';
    let area: number;

    if (rule === TrapezoidalRule.LinearTrapezoidal) {
      method = 'linear';
      area = ((c1 + c2) / 2) * dt;
    } else {
      const isPostCmax = i >= cmaxIndex;
      const useLog = (rule === TrapezoidalRule.LinearUpLogDown && isPostCmax) ||
        (rule === TrapezoidalRule.LinearLog && isPostCmax);

      if (useLog && c1 > 0 && c2 > 0 && c1 !== c2) {
        method = 'log';
        area = ((c1 - c2) / (Math.log(c1) - Math.log(c2))) * dt;
      } else {
        // Log rule undefined at zero, or pre-Cmax, or equal concentrations.
        method = 'linear';
        area = ((c1 + c2) / 2) * dt;
      }
    }

    cumulative += area;
    segments.push({
      startIndex: i,
      endIndex: i + 1,
      width: dt,
      height: (c1 + c2) / 2,
      area,
      method,
      cumulative,
    });
  }

  const aucZeroToT = segments.reduce((sum, s) => sum + s.area, 0);
  return { aucZeroToT, segments, missingLloq: anyMissingLloq };
}
